// Per-grass blade illustrations used on the guides listing card header.
// Decorative SVGs, monochrome, tinted by the consumer via the `color` prop.
// viewBox: 0 0 200 120 — wide aspect that sits in the tinted card header.

import type { ComponentType, SVGProps } from "react"

interface BladeProps {
  color?: string
  className?: string
}

const Frame = ({ children, ...rest }: SVGProps<SVGSVGElement> & { children: React.ReactNode }) => (
  <svg
    viewBox="0 0 200 120"
    width="100%"
    height="100%"
    preserveAspectRatio="xMidYMax meet"
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
)

const Baseline = ({ color }: { color: string }) => (
  <line x1="20" y1="111" x2="180" y2="111" stroke={color} strokeWidth="1" opacity="0.25" strokeLinecap="round" />
)

const KentuckyBluegrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M100 110 C 99 70 102 40 100 14" strokeWidth="3.2" />
      <path d="M82 110 C 80 80 78 50 72 28" strokeWidth="2.6" />
      <path d="M118 110 C 120 80 124 50 130 26" strokeWidth="2.6" />
      <path d="M62 110 C 58 88 52 70 46 52" strokeWidth="2.2" />
      <path d="M138 110 C 142 88 148 70 154 50" strokeWidth="2.2" />
      <path d="M48 110 C 44 96 38 84 32 76" strokeWidth="1.8" />
      <path d="M152 110 C 156 96 162 84 168 76" strokeWidth="1.8" />
      <circle cx="100" cy="14" r="2" fill={color} stroke="none" opacity="0.85" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const TallFescue: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M70 110 L 68 16" strokeWidth="4.2" />
      <path d="M100 110 L 100 8" strokeWidth="4.6" />
      <path d="M130 110 L 134 18" strokeWidth="4.2" />
      <path d="M50 110 L 44 36" strokeWidth="3.4" />
      <path d="M150 110 L 158 36" strokeWidth="3.4" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const FineFescue: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="1.4">
      <path d="M40 110 C 38 70 44 40 36 12" />
      <path d="M55 110 C 56 80 50 50 60 18" />
      <path d="M70 110 C 72 70 66 40 76 10" />
      <path d="M85 110 C 86 80 92 50 82 14" />
      <path d="M100 110 C 100 70 96 40 104 8" />
      <path d="M115 110 C 116 80 110 50 120 16" />
      <path d="M130 110 C 132 70 126 40 136 12" />
      <path d="M145 110 C 146 80 152 50 142 20" />
      <path d="M160 110 C 162 80 156 50 166 18" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const PerennialRyegrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="2.2">
      <path d="M60 110 L 58 18" />
      <path d="M75 110 L 76 12" />
      <path d="M90 110 L 89 8" />
      <path d="M105 110 L 106 14" />
      <path d="M120 110 L 119 10" />
      <path d="M135 110 L 137 18" />
      <path d="M48 110 L 44 30" strokeWidth="1.8" />
      <path d="M150 110 L 154 28" strokeWidth="1.8" />
    </g>
    <g fill={color} opacity="0.4">
      <circle cx="76" cy="40" r="1.2" />
      <circle cx="106" cy="34" r="1.2" />
      <circle cx="119" cy="38" r="1.2" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const Bermudagrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M50 100 L 46 30" strokeWidth="1.8" />
      <path d="M65 102 L 64 22" strokeWidth="2" />
      <path d="M82 100 L 84 28" strokeWidth="1.8" />
      <path d="M100 102 L 100 16" strokeWidth="2.2" />
      <path d="M118 100 L 116 28" strokeWidth="1.8" />
      <path d="M135 102 L 136 22" strokeWidth="2" />
      <path d="M150 100 L 154 30" strokeWidth="1.8" />
      <path d="M30 102 C 60 106, 100 100, 140 104 S 180 100, 188 102" strokeWidth="2" />
      <g fill={color} stroke="none">
        <circle cx="50" cy="103" r="1.8" />
        <circle cx="82" cy="102" r="1.8" />
        <circle cx="118" cy="102" r="1.8" />
        <circle cx="150" cy="103" r="1.8" />
      </g>
    </g>
    <Baseline color={color} />
  </Frame>
)

const Zoysiagrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill={color} stroke="none">
      <path d="M40 110 L 46 30 L 50 110 Z" />
      <path d="M55 110 L 60 22 L 64 110 Z" />
      <path d="M70 110 L 76 18 L 80 110 Z" />
      <path d="M84 110 L 92 12 L 96 110 Z" />
      <path d="M100 110 L 106 18 L 110 110 Z" />
      <path d="M115 110 L 122 14 L 126 110 Z" />
      <path d="M130 110 L 138 22 L 142 110 Z" />
      <path d="M146 110 L 152 30 L 156 110 Z" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const StAugustinegrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill={color} stroke="none">
      <path d="M60 110 C 50 80, 48 50, 56 22 C 62 18, 70 22, 70 36 C 70 70, 70 95, 72 110 Z" />
      <path d="M90 110 C 80 80, 80 45, 92 14 C 100 10, 108 16, 106 32 C 104 70, 104 95, 104 110 Z" />
      <path d="M125 110 C 120 80, 122 50, 134 24 C 142 22, 148 30, 144 44 C 138 75, 138 96, 140 110 Z" />
    </g>
    <g fill="none" stroke="#fff" strokeWidth="0.8" opacity="0.4" strokeLinecap="round">
      <path d="M64 108 C 60 80, 58 50, 62 24" />
      <path d="M97 108 C 92 80, 92 48, 99 18" />
      <path d="M132 108 C 128 80, 130 52, 138 28" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const Centipedegrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="2.4">
      <path d="M50 110 C 50 90, 48 70, 52 56" />
      <path d="M68 110 C 68 88, 66 66, 70 48" />
      <path d="M86 110 C 86 86, 84 64, 88 44" />
      <path d="M104 110 C 104 88, 102 66, 106 48" />
      <path d="M122 110 C 122 86, 120 62, 124 42" />
      <path d="M140 110 C 140 88, 138 66, 142 48" />
      <path d="M156 110 C 156 90, 154 72, 158 56" />
    </g>
    <path d="M28 102 L 172 102" stroke={color} strokeWidth="1.6" opacity="0.55" strokeLinecap="round" fill="none" />
    <g fill={color} stroke="none" opacity="0.55">
      <circle cx="50" cy="102" r="1.6" />
      <circle cx="86" cy="102" r="1.6" />
      <circle cx="122" cy="102" r="1.6" />
      <circle cx="156" cy="102" r="1.6" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const Bahiagrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M55 110 L 50 30" strokeWidth="2.6" />
      <path d="M75 110 L 78 24" strokeWidth="2.8" />
      <path d="M125 110 L 122 26" strokeWidth="2.8" />
      <path d="M145 110 L 150 32" strokeWidth="2.6" />
      <path d="M100 110 L 100 50" strokeWidth="2.4" />
      <path d="M100 50 L 84 14" strokeWidth="1.8" />
      <path d="M100 50 L 116 14" strokeWidth="1.8" />
    </g>
    <g fill={color} stroke="none">
      <circle cx="86" cy="20" r="1.6" />
      <circle cx="90" cy="28" r="1.6" />
      <circle cx="94" cy="36" r="1.6" />
      <circle cx="114" cy="20" r="1.6" />
      <circle cx="110" cy="28" r="1.6" />
      <circle cx="106" cy="36" r="1.6" />
    </g>
    <Baseline color={color} />
  </Frame>
)

const Buffalograss: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="1.8">
      <path d="M44 110 C 40 84, 56 70, 48 50 C 42 34, 54 28, 50 18" />
      <path d="M66 110 C 70 84, 56 68, 64 48 C 70 34, 60 26, 70 14" />
      <path d="M88 110 C 84 84, 100 68, 92 48 C 86 34, 96 26, 90 12" />
      <path d="M110 110 C 114 84, 100 68, 108 50 C 114 36, 104 28, 114 16" />
      <path d="M132 110 C 128 84, 144 70, 134 50 C 128 36, 138 28, 130 14" />
      <path d="M154 110 C 158 86, 144 70, 152 52 C 158 38, 148 30, 156 18" />
    </g>
    <Baseline color={color} />
  </Frame>
)

// Annual ryegrass: upright medium blades plus a herringbone seedhead spike —
// the tall bristly seed spike is its signature, and distinguishes it from the
// plain uprights of perennial ryegrass.
const AnnualRyegrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M62 110 L 56 26" strokeWidth="2.2" />
      <path d="M78 110 L 74 18" strokeWidth="2.4" />
      <path d="M122 110 L 126 20" strokeWidth="2.4" />
      <path d="M138 110 L 144 28" strokeWidth="2.2" />
      <path d="M48 110 L 40 40" strokeWidth="1.8" />
      <path d="M152 110 L 160 40" strokeWidth="1.8" />
      <path d="M100 110 L 100 18" strokeWidth="2.6" />
    </g>
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="1.6">
      <path d="M100 30 L 89 26" />
      <path d="M100 38 L 111 34" />
      <path d="M100 46 L 89 42" />
      <path d="M100 54 L 111 50" />
      <path d="M100 62 L 90 58" />
      <path d="M100 70 L 110 66" />
    </g>
    <Baseline color={color} />
  </Frame>
)

// Mixed cool-season blend: intentionally heterogeneous blade widths — one broad
// fescue-style blade, medium ryegrass/bluegrass uprights, and fine curved
// fescue threads — so the silhouette itself reads as "a blend."
const MixedCoolSeasonBlend: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M64 110 L 60 20" strokeWidth="4.4" />
      <path d="M92 110 L 94 12" strokeWidth="3" />
      <path d="M116 110 C 118 78 122 46 128 22" strokeWidth="2.6" />
      <path d="M156 110 L 160 44" strokeWidth="2.2" />
      <path d="M44 110 C 42 74 48 46 40 22" strokeWidth="1.3" />
      <path d="M78 110 C 79 80 74 52 82 30" strokeWidth="1.3" />
      <path d="M104 110 C 105 82 108 56 100 34" strokeWidth="1.3" />
      <path d="M140 110 C 142 80 138 52 148 30" strokeWidth="1.5" />
    </g>
    <Baseline color={color} />
  </Frame>
)

// Kikuyugrass: a creeping stolon with coarse blade fans erupting from each node —
// the aggressive, node-clustered spread that sets it apart from bermuda's thin,
// evenly-spaced uprights.
const Kikuyugrass: ComponentType<BladeProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <g fill="none" stroke={color} strokeLinecap="round">
      <path d="M22 104 C 58 100, 100 106, 142 102 S 180 104, 188 102" strokeWidth="2.6" />
      <path d="M64 103 L 48 44" strokeWidth="3" />
      <path d="M64 103 L 58 26" strokeWidth="3.2" />
      <path d="M64 103 L 72 24" strokeWidth="3.2" />
      <path d="M64 103 L 84 42" strokeWidth="3" />
      <path d="M124 102 L 108 46" strokeWidth="3" />
      <path d="M124 102 L 118 28" strokeWidth="3.2" />
      <path d="M124 102 L 132 26" strokeWidth="3.2" />
      <path d="M124 102 L 144 44" strokeWidth="3" />
    </g>
    <g fill={color} stroke="none">
      <circle cx="64" cy="103" r="2.4" />
      <circle cx="124" cy="102" r="2.4" />
    </g>
    <Baseline color={color} />
  </Frame>
)

export const GRASS_BLADES: Record<string, ComponentType<BladeProps>> = {
  "kentucky-bluegrass": KentuckyBluegrass,
  "tall-fescue": TallFescue,
  "fine-fescue": FineFescue,
  "perennial-ryegrass": PerennialRyegrass,
  "annual-ryegrass": AnnualRyegrass,
  "mixed-cool-season-blend": MixedCoolSeasonBlend,
  "bermudagrass": Bermudagrass,
  "kikuyugrass": Kikuyugrass,
  "zoysiagrass": Zoysiagrass,
  "st-augustinegrass": StAugustinegrass,
  "centipede": Centipedegrass,
  "bahiagrass": Bahiagrass,
  "buffalograss": Buffalograss,
}
